import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../lib/logger';

export function useAudioCleanup() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioNodesRef = useRef<AudioNode[]>([]);
  const audioBuffersRef = useRef<AudioBuffer[]>([]);
  const isSubscribed = useRef(true);

  const cleanup = useCallback(() => {
    isSubscribed.current = false;

    // Clean up audio nodes
    audioNodesRef.current.forEach(node => {
      try {
        node.disconnect();
      } catch (error) {
        logger.warn('Error disconnecting audio node:', error);
      }
    });
    audioNodesRef.current = [];

    // Clear audio buffers
    audioBuffersRef.current = [];

    // Close audio context
    if (audioContextRef.current?.state !== 'closed') {
      try {
        audioContextRef.current?.close();
      } catch (error) {
        logger.warn('Error closing audio context:', error);
      }
    }
    audioContextRef.current = null;

    logger.info('Audio resources cleaned up');
  }, []);

  const registerNode = useCallback((node: AudioNode) => {
    if (isSubscribed.current) {
      audioNodesRef.current.push(node);
    }
  }, []);

  const registerBuffer = useCallback((buffer: AudioBuffer) => {
    if (isSubscribed.current) {
      audioBuffersRef.current.push(buffer);
    }
  }, []);

  useEffect(() => {
    isSubscribed.current = true;
    return cleanup;
  }, [cleanup]);

  return {
    audioContext: audioContextRef,
    registerNode,
    registerBuffer,
    cleanup
  };
}